/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Col, Row, Collapse, ModalBody, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';

import useApi from 'hooks/useApi';
import { saveAttachments } from 'redux/actions/notificationReviewActions';

import AlertModal from 'shared/components/AlertModal';
import DragDrop from 'shared/components/DragDrop';
import { LabelError } from 'shared/components/Labels';
import { colors } from 'shared/defaultTheme';
import { ButtonIcon } from 'shared/components/Buttons';
import FileSize from 'shared/helpers/fileSize';
import { Checkbox } from 'shared/components/form/CheckBox';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { FileSizeNumber } from 'shared/helpers/fileSize/fileSize';
import {
  toBase64,
  getIconAccessDocument,
  getIconCode,
  getIconDoc,
  getIconExcel,
  getIconImage,
  getIconPdf,
  getIconPpt,
  getIconSound,
  getIconVideo,
  getIconZip,
} from 'shared/utils';

import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as FolderIcon } from 'assets/icon-folder.svg';
import { ReactComponent as OpenFolderIcon } from 'assets/icon-folder-open.svg';
import { ReactComponent as CaretDownIcon } from 'assets/icon-angle-down.svg';
import { ReactComponent as CaretUpIcon } from 'assets/icon-angle-up.svg';
import { ReactComponent as SendIcon } from 'assets/icon-send.svg';
import { ReactComponent as ContextualMenuIcon } from 'assets/icon-contextual-menu.svg';
import { ReactComponent as CheckRoundFullIcon } from 'assets/icon-check-round-full.svg';
import { ReactComponent as HtmlIcon } from 'assets/icon-html.svg';
import { ReactComponent as SoundIcon } from 'assets/icon-sound.svg';
import { ReactComponent as VideoIcon } from 'assets/icon-video.svg';
import { ReactComponent as AccessDocumentIcon } from 'assets/icon-access-document.svg';
import { ReactComponent as PowerPointIcon } from 'assets/icon-ppt.svg';
import { ReactComponent as ZipIcon } from 'assets/icon-zip.svg';

import FolderList from './FolderList';

import {
  AttachFormWrapper,
  ButtonAction,
  Divider,
  DivFileNameAttachForm,
  ActionWraper,
  BoxAttach,
  TopbarAttach,
  ScrollAttach,
  TitleClient,
  SerachDocument,
  ItemDataWrapper,
  Document,
  FolderListModal,
} from './styles';

const PATH_ID = '1/';

const AttachForm = ({ onClose, clientId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { cusNavId, isCustomer, mail } = useSelector((state) => state.auth);
  const { saveFiles } = useSelector((state) => state.notification);
  const { attachments } = useSelector((state) => state.notificationReview);
  const [currentClient, setCurrentClient] = useState();
  const [currentFile, setCurrentFile] = useState();
  const [items, setItems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filter, setFilter] = useState('');
  const [uploadedError, setUploadedError] = useState('');
  const [openFolderLisModal, setOpenFolderLisModal] = useState(false);
  const [showError, setShowError] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState(attachments.length > 0 ? attachments : []);
  const [folderRoot, setFolderRoot] = useState({});

  const [, getItemsByClient] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getClient] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  useEffect(() => {
    const fetchClient = async () => {
      const {
        data: { data },
      } = await getClient({ url: `${window.API_V1}/cli/clients/${cusNavId}/${clientId}` });
      setCurrentClient(data);
    };
    fetchClient();
  }, []);

  const getItems = async () => {
    try {
      const filters = [
        {
          model: 'PathItem',
          field: 'ClientId',
          filter: [clientId],
        },
      ];

      const {
        data: { data: itemsData },
      } = await getItemsByClient({
        data: {
          filters,
          reduce: {
            limit: 0,
            skip: 0,
          },
        },
      });
      const data = itemsData || [];
      setFolderRoot(data.find((x) => x.pathId === PATH_ID));
      setItems(
        data
          ? data
              .sort((a, b) => !b.isFile - !a.isFile || a.name.localeCompare(b.name))
              .map((item) => (item.hasChild ? { ...item, isOpen: false } : item))
          : []
      );
    } catch (ex) {
      console.error('Error getting custom items: ', ex);
    }
  };

  useEffect(() => {
    (async () => {
      await getItems();
    })();
  }, []);

  const getCaretIcon = (item) => {
    if (item.hasChild) {
      return item.isOpen ? <CaretUpIcon className="caret" /> : <CaretDownIcon className="caret" />;
    }
    return null;
  };

  const searchDocuments = async (e) => {
    if (e) {
      const { value } = e.target;
      setFilter(value);
      setSearchValue(value);
      try {
        const {
          data: { data },
        } = await getItemsByClient({
          data: {
            filters: [
              {
                model: 'PathItem',
                field: 'ClientId',
                filter: [clientId],
              },
              {
                model: 'PathItem',
                field: 'IsFile',
                filter: [true],
              },
              {
                model: 'PathItem',
                field: 'Name',
                filter: [value],
                isRegex: true,
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        setDocuments(data || []);
      } catch (ex) {
        console.error('Error getting custom items: ', ex);
      }
    } else {
      getItems();
      setFilter('');
      setSearchValue('');
    }
  };

  const onErrorLogoUploading = (error) => {
    setUploadedError(error);
  };

  useEffect(() => {
    saveFiles.forEach((item) => {
      setSelectedFiles(
        selectedFiles.map((selectedFile) => {
          if (!selectedFile.isBD && selectedFile.name === item.name) {
            return { ...selectedFile, isShare: true, id: item.id };
          }
          return selectedFile;
        })
      );
    });
  }, [saveFiles]);

  const saveDocumentReducer = async (files) => {
    const newFilesPromises = files.map((file) => {
      return new Promise((resolve) => {
        toBase64(file).then((currentFileBase64) => {
          const type = file.name.split('.');
          resolve({
            id: uuid(),
            name: file.name,
            type: `.${type[type.length - 1]}`,
            isBD: false,
            isShare: false,
            size: file.size,
            permissions: [
              {
                u: mail,
                p: 'rw-',
              },
            ],
            data: currentFileBase64,
            created: new Date(),
            isValidFile: true,
          });
        });
      });
    });
    Promise.all(newFilesPromises).then((newFiles) => {
      setSelectedFiles([...selectedFiles, ...newFiles]);
    });
  };

  const getUploadSize = (files) => {
    let totalSizeUpload = 0;
    files.forEach((file) => {
      const megaBytes = FileSizeNumber(file.size);
      totalSizeUpload += parseInt(megaBytes, 10);
    });
    if (totalSizeUpload > 124) {
      setMessageError(t('new_document_text_maximum_size_upload'));
      setShowError(true);
      return false;
    }
    return true;
  };

  const getFiles = (files) => {
    if (files.length + selectedFiles?.length <= 10) {
      if (selectedFiles.length > 0) {
        let newFiles = [];
        files.forEach((selectedFile) => {
          const existFile = selectedFiles.some((x) => x.name === selectedFile.name);
          if (!existFile) {
            newFiles = [...newFiles, selectedFile];
          }
        });
        const validateUploadFile = getUploadSize([...newFiles, ...selectedFiles]);
        if (validateUploadFile) {
          const filterNewFiles = newFiles.filter((x) => x.size > 0);
          const existFileNull = newFiles.some((x) => x.size === 0);
          if (existFileNull) {
            setMessageError(t('new_document_file_null'));
            setShowError(true);
          }
          saveDocumentReducer(filterNewFiles);
        }
      }
      if (selectedFiles.length === 0) {
        const validateUploadFile = getUploadSize(files);
        if (validateUploadFile) {
          const filterFiles = files.filter((x) => x.size > 0);
          const existFileNull = files.some((x) => x.size === 0);
          if (existFileNull) {
            setMessageError(t('new_document_file_null'));
            setShowError(true);
          }
          saveDocumentReducer(filterFiles);
        }
      }
    } else {
      setMessageError(t('document_up_file_uploaded_maximum_size'));
      setShowError(true);
    }
  };

  const removeFile = (file, index) => {
    const newFiles = selectedFiles.filter((item, i) => i !== index && item.id !== index);
    setSelectedFiles(newFiles);
  };

  const onSaveFile = () => {
    dispatch(saveAttachments(selectedFiles));
    onClose();
  };

  const getCurrentItem = (itemId) => {
    setItems(items.map((item) => (item.id === itemId ? { ...item, isOpen: !item.isOpen } : item)));
  };

  const selectedClientFile = useCallback((e, item) => {
    const findFile = selectedFiles.find((x) => x.id === item.id);
    if (!findFile) {
      const file = {
        id: item.id,
        pathId: PATH_ID,
        name: item.name,
        size: item.size,
        type: item.type,
        permissions: [
          {
            u: mail,
            p: 'rw-',
          },
        ],
        data: item.data,
        isBD: true,
        isShare: false,
        isSelectedFile: true,
      };
      const newFiles = [...selectedFiles, file];
      if (newFiles.length <= 10) {
        setSelectedFiles(newFiles);
      } else {
        setMessageError(t('document_up_file_uploaded_maximum_size'));
        setShowError(true);
      }
    } else {
      removeFile(findFile, findFile.id);
    }
  });

  const onCloseFolderLisModal = () => {
    setOpenFolderLisModal(false);
  };

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{t('documents_upload_document')}</h3>
        </Col>
      </Row>
    );
  };

  const ItemTree = ({ listItem, level }) => {
    return listItem
      ? listItem.map((item) =>
          item.id ? (
            <React.Fragment key={item.id}>
              <ItemDataWrapper level={level} onClick={() => getCurrentItem(item.id)}>
                <div id={`toggf${item.id}`} className="info-container">
                  {item.isFile ? (
                    <>
                      <Checkbox
                        type="checkbox"
                        id="filterCheckAll"
                        label=""
                        checked={selectedFiles.some((file) => file.id === item.id)}
                        onClick={(e) => selectedClientFile(e, item)}
                      />
                      <span className="text-file">{item.name}</span>
                    </>
                  ) : (
                    <>
                      {item.isOpen && item.hasChild ? (
                        <OpenFolderIcon className="folder-icon" />
                      ) : (
                        <FolderIcon className="folder-icon" />
                      )}
                      <span className="text-folder">{item.name}</span>
                    </>
                  )}
                </div>
                {item.isFile ? null : getCaretIcon(item)}
              </ItemDataWrapper>
              <Collapse isOpen={item.isOpen}>
                {items?.some((currentItem) => {
                  const pathIds = currentItem.pathId.split('/');
                  const clearedPathIds = pathIds.filter((x) => x.length > 0);
                  const parentId = clearedPathIds[clearedPathIds.length - 2];
                  return parentId === item.id;
                }) ? (
                  <ItemTree
                    listItem={items?.filter((currentItem) => {
                      const pathIds = currentItem.pathId.split('/');
                      const clearedPathIds = pathIds.filter((x) => x.length > 0);
                      const parentId = clearedPathIds[clearedPathIds.length - 2];
                      return parentId === item.id;
                    })}
                    level={level + 1}
                  />
                ) : null}
              </Collapse>
            </React.Fragment>
          ) : null
        )
      : null;
  };

  const IconFile = ({ file }) => {
    if (getIconDoc(file.type)) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPdf(file.type)) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconImage(file.type)) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconCode(file.type)) {
      return <HtmlIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconSound(file.type)) {
      return <SoundIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconVideo(file.type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconAccessDocument(file.type)) {
      return (
        <AccessDocumentIcon
          fill={colors.suvaGrey}
          className="control-panel__icon-type"
          width="20"
        />
      );
    }
    if (getIconPpt(file.type)) {
      return (
        <PowerPointIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />
      );
    }
    if (getIconZip(file.type)) {
      return <ZipIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconExcel(file.type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  return (
    <AttachFormWrapper>
      <Title />
      <Row className="row-content w-100">
        <Col sm={12}>
          <form className="form form-field" noValidate>
            <Row className="w-100">
              <Col sm={12}>
                <Row>
                  <Col xl={isCustomer ? 5 : 12} lg={isCustomer ? 5 : 12} md={12} sm={12} xs={12}>
                    <div className="mb-2 mr-3">
                      <DragDrop
                        onChangeFile={(files) => getFiles(files)}
                        onError={onErrorLogoUploading}
                      />
                      {uploadedError && <LabelError label={uploadedError} />}
                    </div>
                    {selectedFiles.length > 0 && (
                      <BoxAttach className="mb-4">
                        <TopbarAttach>
                          <h5>{t('attach_form_selected_documents')}</h5>
                        </TopbarAttach>
                        <ScrollAttach>
                          {selectedFiles.length > 0 && <hr />}
                          {selectedFiles.length > 0
                            ? selectedFiles.map((file, index) => (
                                <div key={file.id}>
                                  <div className="d-flex ml-2">
                                    <div>
                                      <IconFile file={file} />
                                    </div>
                                    <div className="d-flex justify-content-between w-100">
                                      <DivFileNameAttachForm>
                                        <span className="ml-1">{file.name}</span>
                                      </DivFileNameAttachForm>
                                      <div className="d-flex">
                                        <div>
                                          {file.isBD && (
                                            <>
                                              <ContextualMenuIcon
                                                width={20}
                                                className="mr-2"
                                                id="classification-document"
                                              />
                                              <UncontrolledTooltip
                                                flip
                                                target="classification-document"
                                              >
                                                {t('documents_classification')}
                                              </UncontrolledTooltip>
                                            </>
                                          )}
                                          {!file.isBD && file.isShare && file.id && (
                                            <CheckRoundFullIcon width={15} className="mr-2" />
                                          )}
                                          <span>{FileSize(file.size)}</span>
                                        </div>
                                        <Divider className="ml-2" />
                                        <div className="d-flex">
                                          {!file.isBD && !file.isShare && (
                                            <ButtonIcon
                                              id="share-document-folder"
                                              title={t('general_button_save')}
                                              type="button"
                                              Icon={() => <SendIcon fill={colors.suvaGrey} />}
                                              className="button-icon"
                                              onClick={() => {
                                                setOpenFolderLisModal(true);
                                                setCurrentFile(file);
                                              }}
                                            />
                                          )}
                                          <ButtonIcon
                                            id="delete-document-notification"
                                            title={t('documents_delete')}
                                            type="button"
                                            Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                                            onClick={() => removeFile(file, index)}
                                            className="button-icon"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <hr />
                                </div>
                              ))
                            : null}
                        </ScrollAttach>
                      </BoxAttach>
                    )}
                  </Col>
                  {isCustomer && (
                    <Col xl={7} lg={7} md={12} sm={12} xs={12}>
                      <TitleClient>
                        {' '}
                        {t('attach_form_attach_document').replace(
                          '{clientName}',
                          `${currentClient?.clientName}`
                        )}
                      </TitleClient>
                      <SerachDocument
                        Icon={() => null}
                        borderRadius={2}
                        className="mb-4 w-25 m-0 h-50"
                        inputClassName="w-100"
                        buttonClassName=""
                        withButtonClear
                        placeholder={t('attach_form_document_search')}
                        rightIcon={<SearchIcon fill={colors.black} />}
                        onChange={searchDocuments}
                        value={searchValue ?? ''}
                      />
                      {filter === '' ? (
                        <div className="item-tree-folder">
                          <ItemTree
                            listItem={items?.filter((item) => {
                              const pathIds = item.pathId.split('/');
                              const clearedPathIds = pathIds.filter((x) => x.length > 0);
                              const parentId = clearedPathIds[clearedPathIds.length - 2];
                              return parentId === folderRoot.id;
                            })}
                            level={1}
                          />
                        </div>
                      ) : (
                        <div className="item-tree-folder">
                          {documents.map((document) => (
                            <Document>
                              <Checkbox
                                type="checkbox"
                                id="filterCheckAll"
                                label=""
                                checked={selectedFiles.some((file) => file.id === document.id)}
                                onClick={(e) => selectedClientFile(e, document)}
                              />
                              <span>{document.name}</span>
                            </Document>
                          ))}
                        </div>
                      )}
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
            <ActionWraper className="w-100">
              <Col className="d-flex flex-row justify-content-end align-items-end" sm={12}>
                <ButtonAction
                  className="text-uppercase mr-4"
                  type="button"
                  color="secondary"
                  label={t('general_button_cancel')}
                  onClick={() => onClose()}
                />
                <ButtonAction
                  className="text-uppercase"
                  type="button"
                  color="primary"
                  label={t('general_button_ok')}
                  onClick={onSaveFile}
                />
              </Col>
            </ActionWraper>
          </form>
        </Col>
      </Row>
      <FolderListModal isOpen={openFolderLisModal} toggle={onCloseFolderLisModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFolderLisModal}
        />
        <ModalBody>
          <FolderList
            onClose={onCloseFolderLisModal}
            items={items}
            currentFile={currentFile}
            clientId={clientId}
          />
        </ModalBody>
      </FolderListModal>
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={messageError}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
    </AttachFormWrapper>
  );
};

AttachForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired,
};

export default AttachForm;
